/*
 * ALT-SHEET-DRAG — makes the mobile bottom-sheet grabber real.
 *
 * `.drawer__inner::before` (and the quick-view's equivalent) draws a drag
 * handle, but nothing listened for the gesture: the sheet looked draggable and
 * was not. That is a false affordance, which the project rules forbid — either
 * the handle works or it should not be drawn. This makes it work.
 *
 * Scope: mobile bottom-sheet breakpoint only. On desktop the panel is a
 * right-hand side-slide with no handle, so the gesture is not bound at all.
 *
 * Only `transform` is animated (never height/top), so the drag stays on the
 * compositor. The CSS transition is suspended while the finger is down and
 * restored on release, otherwise every frame would fight a 340ms ease.
 */
(function () {
  var SHEET_MQ = window.matchMedia('(max-width: 749px)');
  var REDUCE = window.matchMedia('(prefers-reduced-motion: reduce)');

  // Distance past which the release closes instead of snapping back, and the
  // flick speed that closes regardless of distance.
  var CLOSE_RATIO = 0.28;
  var FLICK_VELOCITY = 0.55; // px per ms

  /* A real element, not the ::before pseudo. A pseudo-element cannot be
     hit-tested or carry `touch-action`, so the browser claimed the vertical pan
     as a scroll and fired pointercancel — which is why the sheet never dragged.
     This grabber owns `touch-action: none`, so the gesture reaches us. */
  function ensureHandle(panel) {
    var existing = panel.querySelector(':scope > .alt-sheet-handle');
    if (existing) return existing;
    var handle = document.createElement('div');
    handle.className = 'alt-sheet-handle';
    handle.setAttribute('aria-hidden', 'true');
    panel.insertBefore(handle, panel.firstChild);
    return handle;
  }

  function bindSheet(panel, onDismiss) {
    if (!panel || panel.dataset.altSheetDrag === 'on') return;
    if (!SHEET_MQ.matches) return;
    panel.dataset.altSheetDrag = 'on';
    var handle = ensureHandle(panel);

    var startY = 0;
    var startT = 0;
    var dy = 0;
    var dragging = false;
    var prevTransition = '';

    handle.addEventListener(
      'pointerdown',
      function (event) {
        if (!SHEET_MQ.matches) return;
        dragging = true;
        startY = event.clientY;
        startT = event.timeStamp;
        dy = 0;
        prevTransition = panel.style.transition;
        panel.style.transition = 'none';
        handle.setPointerCapture(event.pointerId);
      },
      { passive: true }
    );

    handle.addEventListener(
      'pointermove',
      function (event) {
        if (!dragging) return;
        dy = Math.max(0, event.clientY - startY); // downward only
        panel.style.transform = 'translateY(' + dy + 'px)';
      },
      { passive: true }
    );

    function end(event) {
      if (!dragging) return;
      dragging = false;
      panel.style.transition = prevTransition;
      panel.style.transform = '';

      var elapsed = Math.max(1, event.timeStamp - startT);
      var velocity = dy / elapsed;
      var far = dy > panel.getBoundingClientRect().height * CLOSE_RATIO;

      if (far || velocity > FLICK_VELOCITY) onDismiss();
      // Otherwise the cleared inline transform lets the CSS resting state
      // snap the sheet back to place.
    }

    handle.addEventListener('pointerup', end, { passive: true });
    handle.addEventListener('pointercancel', end, { passive: true });

    if (REDUCE.matches) panel.dataset.altSheetDrag = 'reduced';
  }


  /* Mirrors the quick-view's [open] state onto <body> so the drawer underneath
     can recede. Without this the two panels are the same size and colour and
     the customer cannot perceive that a second level opened. */
  function watchQuickView() {
    var qv = document.querySelector('#CartQuickView');
    if (!qv || qv.dataset.altLayerWatch === 'on') return;
    qv.dataset.altLayerWatch = 'on';
    var sync = function () {
      document.body.classList.toggle('alt-quickview-open', qv.hasAttribute('open'));
    };
    new MutationObserver(sync).observe(qv, { attributes: true, attributeFilter: ['open'] });
    sync();
  }

  function init() {
    var drawer = document.querySelector('cart-drawer');
    if (drawer) {
      bindSheet(drawer.querySelector('.drawer__inner'), function () {
        drawer.close();
      });
    }

    watchQuickView();

    var quickView = document.querySelector('#CartQuickView');
    if (quickView) {
      bindSheet(quickView.querySelector('.quick-add-modal__content'), function () {
        var closer = quickView.querySelector('.quick-add-modal__toggle, [id^="ModalClose-"]');
        if (closer) closer.click();
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // The drawer re-renders its inner markup on every cart update, so re-bind.
  document.addEventListener('cart:refresh', init);
  document.addEventListener('shopify:section:load', init);
})();
